import { Link, useLocation } from 'react-router-dom';
import MotionWrapper from '../MotionWrapper';
import { FiChevronRight, FiChevronLeft, FiSettings, FiUser, FiExternalLink, FiPlus } from 'react-icons/fi';
import { getPublicDisplayName } from '../../utils/displayName';
import { slugify } from '../../lib/slug';

export default function DashboardLayout({ title = 'Dashboard', breadcrumb = [], user, isClient: isClientProp, isCreative: isCreativeProp, showHero = true, children }) {
  const location = useLocation();
  const path = location.pathname || '';

  // Prefer explicit role flags passed from Dashboard wrapper; fallback to user roles
  let isClient = Boolean(isClientProp);
  let isCreative = Boolean(isCreativeProp);
  let isAdmin = false;
  if (isClientProp === undefined || isCreativeProp === undefined) {
    const roles = Array.isArray(user?.roles) ? user.roles : (user?.role ? [user.role] : []);
    const norm = roles.map((r) => {
      const k = String(r || '').trim().toLowerCase();
      if (k === 'creative' || k === 'creator' || k === 'artist') return 'creative';
      if (k === 'client' || k === 'buyer' || k === 'customer') return 'client';
      return k;
    });
    isAdmin = norm.includes('admin');
    isClient = norm.includes('client') || isAdmin;
    isCreative = norm.includes('creative') || isAdmin;
  }

  // Auto-build breadcrumbs from route when none provided
  const autoBreadcrumb = (() => {
    if (breadcrumb && breadcrumb.length) return breadcrumb;
    const labelMap = {
      overview: 'Overview',
      client: 'Client',
      bookings: 'My Bookings',
      favorites: 'Favorites',
      creative: 'Creative',
      orders: 'Orders',
      tasks: 'Tasks',
      messages: 'Messages',
      notifications: 'Notifications',
      users: 'Users',
      settings: 'Settings',
      support: 'Support',
    };
    const segs = path.startsWith('/dashboard') ? path.replace('/dashboard', '').split('/').filter(Boolean) : [];
    const crumbs = [];
    let acc = '/dashboard';

    // Determine primary context crumb (Buyer or Creative) based on route
    const clientRoutes = new Set(['client']);
    const creativeRoutes = new Set(['creative']);
    const first = segs[0];
    if (first) {
      if (clientRoutes.has(first)) {
        crumbs.push({ label: 'Client', to: '/dashboard/client' });
      } else if (creativeRoutes.has(first)) {
        crumbs.push({ label: 'Creative', to: '/dashboard/creative' });
      } else if (first === 'messages' || first === 'notifications') {
        if (isCreative && !isClient) {
          crumbs.push({ label: 'Creative', to: '/dashboard/creative' });
        } else if (isClient && !isCreative) {
          crumbs.push({ label: 'Client', to: '/dashboard/client' });
        } else {
          // Both roles: no persona crumb; just show Messages below
        }
      }
    }
    const isFirstContextOnly = first === 'client' || first === 'creative';

    for (let i = 0; i < segs.length; i++) {
      // Avoid duplicating the first segment label when it matches the context crumb
      if (i === 0 && isFirstContextOnly) continue;
      acc += `/${segs[i]}`;
      const key = segs[i];
      // Skip showing 'Overview' as an extra crumb; base 'Dashboard' covers it
      if (key === 'overview') continue;
      const label = labelMap[key] || key.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
      crumbs.push({ label, to: i < segs.length - 1 ? acc : undefined });
    }
    return crumbs;
  })();

  // Compute title: prefer explicit title, else last breadcrumb label or Dashboard
  const computedTitle = (() => {
    if (title && title !== 'Dashboard') return title;
    const last = autoBreadcrumb[autoBreadcrumb.length - 1]?.label;
    return last || 'Dashboard';
  })();

  // Determine active tab from route (messages stays neutral)
  const clientRoutesSet = new Set(['client']);
  const creativeRoutesSet = new Set(['creative']);
  const segsForActive = path.startsWith('/dashboard') ? path.replace('/dashboard', '').split('/').filter(Boolean) : [];
  const firstSeg = segsForActive[0];
  let activeTab = 'overview';
  if (firstSeg) {
    if (clientRoutesSet.has(firstSeg)) activeTab = 'client';
    else if (creativeRoutesSet.has(firstSeg)) activeTab = 'creative';
    else if (firstSeg === 'messages' || firstSeg === 'notifications') {
      if (isCreative && !isClient) activeTab = 'creative';
      else if (isClient && !isCreative) activeTab = 'client';
      else activeTab = 'overview';
    }
  } else if (path === '/dashboard' || path.startsWith('/dashboard/overview')) {
    activeTab = 'overview';
  }

  // Determine which persona's public profile link should be shown
  // Hide on common dashboard (overview); show specific link on client/creative tabs
  const personaForPublicLink = (() => {
    if (activeTab === 'client') return 'client';
    if (activeTab === 'creative') return 'creative';
    return null; // overview or neutral → hide link
  })();

  // Grouped button-style tabs with rounded container; no bold on active
  const tabClass = (isActive) => (
    `px-4 py-2 text-sm transition-colors ` +
    (isActive ? 'text-primary-text bg-white/90 dark:bg-white/20' : 'text-secondary-text hover:text-primary-text')
  );

  return (
    <div className="min-h-[70vh] bg-white dark:bg-black">
      {/* Main content - header-only, no extra site header */}
      <main className="min-h-[70vh] px-4 sm:px-6 md:px-8 py-6">
        {/* Hero with welcome and tab switch (uses creatives-style gradient) */}
        {showHero && (
        <div className="card p-6 mb-4 card-header-gradient border-card-border">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-secondary-bg border border-card-border overflow-hidden flex items-center justify-center">
                {(() => {
                  const displayName = (user?.display_name || user?.first_name || user?.email || 'User');
                  const initials = String(displayName).trim().split(/\s+/).slice(0,2).map(s => s[0]?.toUpperCase() || '').join('') || 'U';
                  const avatarUrl = user?.avatar_url || user?.avatar || '';
                  if (avatarUrl) {
                    return <img src={avatarUrl} alt={displayName} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.src = '/logo.svg'; }} />;
                  }
                  return <span className="text-sm font-medium text-primary-text">{initials}</span>;
                })()}
              </div>
              <div>
                <div className="text-sm text-secondary-text">Welcome</div>
                <div className="text-2xl md:text-3xl font-semibold text-primary-text">{(user?.display_name || user?.first_name || user?.email || 'User')}</div>
                {user?.email && <div className="text-xs text-secondary-text mt-1">{user.email}</div>}
                {/* Timezone displayed subtly */}
                {user && <div className="text-xs text-secondary-text mt-1">Timezone: <span className="font-medium">{Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'}</span></div>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Register as Creative/Client buttons - only show if user doesn't have the role */}
              {!isCreative && !isAdmin && (
                <Link 
                  to="/add-role?role=creative" 
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-primary text-white hover:bg-primary/90 text-xs font-medium"
                >
                  <FiPlus className="w-4 h-4" />
                  <span>Become a Creative</span>
                </Link>
              )}
              {!isClient && !isAdmin && (
                <Link 
                  to="/add-role?role=client" 
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 text-xs font-medium"
                >
                  <FiPlus className="w-4 h-4" />
                  <span>Register as Client</span>
                </Link>
              )}
              {/* Show persona-specific public profile link only on client/creative tabs */}
              {personaForPublicLink && (() => {
                const shortFromUuid = (uuid) => {
                  const clean = String(uuid || '').replace(/-/g, '').toLowerCase();
                  if (!/^[a-f0-9]{32}$/.test(clean)) return null;
                  return `${clean.slice(0, 8)}${clean.slice(-4)}`;
                };
                const baseName = getPublicDisplayName({
                  first_name: user?.first_name,
                  last_name: user?.last_name,
                  display_name: user?.display_name,
                  email: user?.email,
                }) || (user?.display_name || user?.first_name || user?.email || 'User');
                const baseSlug = slugify(baseName);
                const token = shortFromUuid(user?.id);
                const prefix = personaForPublicLink === 'client' ? '/client/profile/' : '/creative/profile/';
                const publicPath = token ? `${prefix}${baseSlug}-${token}` : `${prefix}${baseSlug}`;
                return (
                  <Link to={publicPath} className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-primary-text hover:bg-subtle-bg text-xs">
                    <FiExternalLink className="w-4 h-4" />
                    <span>View Public Profile</span>
                  </Link>
                );
              })()}
            </div>
          </div>
          {/* Tabs inside hero (grouped buttons with rounded background, theme-aware on gradient) */}
          <div className="mt-4 inline-flex items-center rounded-xl border border-card-border bg-white/50 dark:bg-white/10 overflow-hidden divide-x divide-card-border">
            <Link to="/dashboard/overview" className={tabClass(activeTab === 'overview')}>Dashboard</Link>
            {isClient && (
              <Link to="/dashboard/client" className={tabClass(activeTab === 'client')}>Client</Link>
            )}
            {isCreative && (
              <Link to="/dashboard/creative" className={tabClass(activeTab === 'creative')}>Creative</Link>
            )}
          </div>
        </div>
        )}
        {/* Breadcrumb + Back link on one row */}
        <div className="flex items-center justify-between text-xs text-secondary-text mb-2">
          <div className="flex items-center gap-2">
            <Link to="/dashboard/overview" className="hover:text-primary-text">Dashboard</Link>
            {autoBreadcrumb.map((b, idx) => (
              <span key={idx} className="flex items-center gap-2">
                <FiChevronRight className="w-3 h-3" />
                {b.to ? <Link to={b.to} className="hover:text-primary-text">{b.label}</Link> : <span>{b.label}</span>}
              </span>
            ))}
          </div>
          {(() => {
            const onOverview = path === '/dashboard' || path.startsWith('/dashboard/overview');
            return !onOverview ? (
              <Link to="/dashboard/overview" className="inline-flex items-center gap-1 text-xs text-secondary-text hover:text-primary-text">
                <FiChevronLeft className="w-3 h-3" />
                <span>Back to Dashboard</span>
              </Link>
            ) : null;
          })()}
        </div>
        <div className="flex items-center mb-3">
          <h1 className="text-2xl font-semibold text-primary-text">{computedTitle}</h1>
        </div>
        <MotionWrapper key={location.pathname}>
          {children}
        </MotionWrapper>
      </main>
    </div>
  );
}
